<script setup lang="ts">
import type { Album, EventVault } from "@/models"
import { computed } from "vue"
import { DrawerContextCard } from "@/components"
import ClipboardButton from "@/components/EventLinksDrawer/ClipboardButton.vue"
import { AppLink, Drawer, Icon, NoData } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { isEventWebsiteUrlModeEnabled, ProductTypes } from "@/models"
import { EventLinksDrawerKey } from "@/symbols"

interface EventLinksDrawerProps {
  album?: Album
  eventVault?: EventVault
  isLoading?: boolean
  drawerKey?: symbol
}

const props = withDefaults(defineProps<EventLinksDrawerProps>(), {
  isLoading: false,
  drawerKey: EventLinksDrawerKey,
})

const emit = defineEmits<{
  (e: "open"): void
  (e: "close"): void
}>()

const { t } = useTranslation()
const isEventVault = computed(() => props.eventVault !== undefined)

const packageLabel = computed(() => ProductTypes.getLabel(props.eventVault?.productKey, props.eventVault?.productName))

const isFreemiumEvent = computed(() => props.eventVault?.freemiumMode)

const { toggle } = useModal(props.drawerKey)

function buildShareUrl(path: string = "") {
  return `${window.location.protocol}//${window.location.host}${path}`
}

// When the event has a vanity slug, guest-facing links should carry it so the
// domain`/slug`-style URL is used (e.g. https://host/my-event/guest/access/123/token).
// Paths are canonical (leading "/"), so insert the slug after the leading slash.
function withSlug(path: string) {
  const slug = props.eventVault?.website?.slug
  if (!slug || !path)
    return path
  if (!isEventWebsiteUrlModeEnabled(props.eventVault?.eventVaultSettings?.eventWebsiteUrlMode))
    return path
  return `/${slug}${path}`
}

const urls = computed(() => {
  const album = props.album
  const eventVault = props.eventVault

  return {
    qrCodeUrl: isEventVault.value ? eventVault?.qrCodeUrl : album?.qrUrl,
    qrDownloadUrl: isEventVault.value
      ? `${import.meta.env.VITE_BASE_API_URL}/qr/${eventVault?.id}`
      : `${import.meta.env.VITE_BASE_API_URL}/qr/${album?.eventVaultId}/album/${album?.id}`,
    shareLink: isEventVault.value ? buildShareUrl(withSlug(eventVault?.shareLink ?? "")) : buildShareUrl(album?.shareLink),
    protectedLink: eventVault?.protectedLink ? buildShareUrl(withSlug(eventVault?.protectedLink)) : null,
    printUrl: isEventVault.value
      ? `/event/print/${eventVault?.id}`
      : `/event/print/${album?.eventVaultId}?album=${album?.id}`,
  }
})
</script>

<template>
  <Drawer
    :name="props.drawerKey" :title="t('links_drawer.event_title')" @open="emit('open')"
    @close="emit('close')"
  >
    <div v-if="isLoading" class="flex flex-col gap-5">
      <div class="gpx-skeleton w-full h-24" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-1/3 h-12" />
    </div>
    <div v-else-if="album || eventVault">
      <DrawerContextCard :event-vault="eventVault" :album="album">
        <div v-if="isFreemiumEvent" class="flex flex-col items-start gap-1.5" data-test="links_drawer_package_info">
          <div v-if="packageLabel || eventVault?.maxPhotoLimit" class="flex flex-col gap-0.5 font-body text-sm text-gray-500">
            <p v-if="packageLabel" data-test="links_drawer_package_type">
              {{ t("links_drawer.package", { package: packageLabel }) }}
            </p>
            <p v-if="eventVault?.maxPhotoLimit" data-test="links_drawer_photo_limit">
              {{ t("links_drawer.max_photos", { limit: eventVault.maxPhotoLimit }) }}
            </p>
          </div>
          <a
            v-if="isFreemiumEvent && eventVault?.upgradeUrl"
            :href="eventVault?.upgradeUrl"
            class="inline-flex items-center gap-1 font-body text-sm text-link hover:text-link-hover hover:underline focus:underline"
            data-test="links_drawer_upgrade_link"
          >
            {{ t("links_drawer.upgrade") }}
            <Icon icon="MoveUpRight" size="xs" />
          </a>
        </div>
      </DrawerContextCard>
      <div class="flex flex-col gap-3 divide-y divide-gray-900">
        <div v-if="urls.qrCodeUrl" class="pb-4">
          <h3 class="font-comfortaa font-bold text-lg">
            {{ t("links_drawer.qr") }}
          </h3>
          <div class="flex">
            <div class="shrink-0 mr-4">
              <img class="w-32 md:w-36 h-32 md:h-36" :src="urls.qrCodeUrl" :alt="t('links_drawer.qr')">
            </div>
            <div class="flex flex-col justify-center gap-2.5">
              <div class="flex items-center font-montserrat font-medium text-sm">
                <Icon icon="CircleInfo" class="shrink-0 mr-2" />{{ t("links_drawer.qr_no_password") }}
              </div>

              <a
                :href="urls.qrDownloadUrl"
                data-test="qr_download_link"
                class="text-link hover:text-link-hover flex items-center hover:underline focus:underline font-montserrat font-medium text-sm"
                download
              >
                <Icon icon="CloudDownload" class="mr-2" />{{ t("links_drawer.download_qr") }}
              </a>
              <a
                :href="urls.printUrl"
                class="text-link hover:text-link-hover flex items-center hover:underline focus:underline font-montserrat font-medium text-sm"
              >
                <Icon icon="Print" class="mr-2" />{{ t("links_drawer.print_qr") }}
              </a>
              <AppLink
                v-if="eventVault?.id" :to="`/event/${eventVault.id}/custom-qr`"
                class="text-link hover:text-link-hover flex items-center hover:underline focus:underline font-montserrat font-medium text-sm"
              >
                <Icon icon="Edit" class="mr-2" />{{ t("links_drawer.customise_qr") }}
              </AppLink>
            </div>
          </div>
        </div>
        <div v-if="urls?.shareLink" class="py-4">
          <div class="flex justify-between mb-3">
            <h3 class="font-comfortaa font-bold text-lg">
              {{ t("links_drawer.magic_link") }}
            </h3>
            <ClipboardButton :content="urls.shareLink">
              {{ t("links_drawer.copy_link") }}
            </ClipboardButton>
          </div>
          <a
            :href="urls.shareLink"
            class="text-link hover:text-link-hover hover:underline focus:underline font-montserrat font-medium text-sm break-all"
            data-test="share_link"
          >
            {{ urls.shareLink }}
          </a>
        </div>
        <div v-if="isEventVault && urls.protectedLink" class="py-4">
          <div class="flex justify-between mb-3">
            <h3 class="font-comfortaa font-bold text-lg">
              {{ t("links_drawer.pin_link") }}
            </h3>
            <ClipboardButton :content="urls.protectedLink">
              {{ t("links_drawer.copy_link") }}
            </ClipboardButton>
          </div>
          <a
            :href="urls.protectedLink"
            class="text-link hover:text-link-hover hover:underline focus:underline font-montserrat font-medium text-sm break-all"
          >
            {{ urls.protectedLink }}
          </a>
          <div class="text-lg font-comfortaa font-bold text-[#B53A3A] mt-1">
            {{ t("links_drawer.pin_code", { code: eventVault?.passcode }) }}
          </div>
        </div>
      </div>
    </div>
    <NoData v-else :button-label="t('close')" @click="toggle" />
  </Drawer>
</template>
